import { LayoutDashboard, LogOut, PanelLeftClose, PanelLeftOpen, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { Button } from '../components/ui/button.tsx';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet.tsx';
import { cn } from '../lib/cn.ts';
import { STATIC_DEMO } from '../lib/config.ts';
import { useT } from '../lib/i18n.tsx';
import { useFilters, useLogout, useMe } from '../lib/queries.ts';
import { useSidebar } from '../lib/sidebar.ts';
import { carryOver } from '../lib/url-state.ts';
import { NAV_ITEMS, navLabel } from './nav.ts';
import { BottomNav } from './BottomNav.tsx';
import { initialsOf, TopBar } from './TopBar.tsx';

/** Non-dismissible: figures from demo data must never be mistaken for real ones. */
function DemoBanner() {
  const t = useT();
  const filters = useFilters();
  if (filters.data?.meta.datasetKind !== 'demo') return null;
  return (
    <div
      role="status"
      className="bg-banner px-4 py-1.5 text-center text-sm font-semibold text-white"
    >
      {t.demo.banner}
    </div>
  );
}

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const t = useT();
  const me = useMe();
  const { search } = useLocation();
  const allowed = NAV_ITEMS.filter((item) => me.data?.permissions.includes(item.permission));
  return (
    <nav aria-label={t.nav.label} className="flex flex-col gap-1">
      {allowed.map((item) => (
        <NavLink
          key={item.to}
          to={{ pathname: item.to, search: carryOver(new URLSearchParams(search)) }}
          end={item.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
              isActive &&
                'bg-accent text-accent-foreground before:absolute before:inset-y-1.5 before:left-0 before:w-1 before:rounded-full before:bg-primary hover:bg-accent hover:text-accent-foreground',
            )
          }
        >
          <item.icon aria-hidden="true" className="size-[18px] shrink-0" />
          {navLabel(t, item)}
        </NavLink>
      ))}
    </nav>
  );
}

function UserMenu() {
  const t = useT();
  const me = useMe();
  const logout = useLogout();
  if (!me.data) return null;
  // The static demo has no server, so there is no session to end.
  if (STATIC_DEMO) return null;
  const name = me.data.displayName;
  return (
    <div className="space-y-1 border-t pt-3">
      <div className="flex items-center gap-3 px-3 py-2">
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
        >
          {initialsOf(name)}
        </span>
        <p className="min-w-0 truncate text-sm font-medium" title={t.nav.signedInAs(name)}>
          {name}
        </p>
      </div>
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="w-full justify-start text-muted-foreground"
      >
        <NavLink to="/security">
          <ShieldCheck aria-hidden="true" className="size-4" />
          {t.security.navLabel}
        </NavLink>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-muted-foreground hover:text-destructive"
        disabled={logout.isPending}
        onClick={() => logout.mutate()}
      >
        <LogOut aria-hidden="true" className="size-4" />
        {t.nav.signOut}
      </Button>
    </div>
  );
}

/** Product mark: a neutral icon tile and the app name (no company branding is invented). */
function Brand() {
  const t = useT();
  return (
    <div className="flex items-center gap-2.5 px-3 pt-1">
      <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <LayoutDashboard aria-hidden="true" className="size-[18px]" />
      </span>
      <span className="text-base font-semibold tracking-tight">{t.app.name}</span>
    </div>
  );
}

/**
 * The content scrolls inside the shell, so the bar and the sidebar never leave the screen. `<main>`
 * is positioned so visually hidden (absolutely positioned) labels stay inside its scroll area
 * instead of stretching the whole page.
 */
function useScrollToTopOnNavigate() {
  const ref = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
  }, [pathname]);
  return ref;
}

/** Large screens: hides or shows the sidebar. Smaller screens use the bottom tabs instead. */
function SidebarToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const t = useT();
  const label = collapsed ? t.nav.showSidebar : t.nav.hideSidebar;
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onToggle}
      aria-label={label}
      aria-expanded={!collapsed}
      aria-controls="sidebar"
      title={label}
      className="hidden lg:inline-flex"
    >
      <Icon aria-hidden="true" className="size-5" />
    </Button>
  );
}

export function AppShell() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const sidebar = useSidebar();
  const { pathname } = useLocation();
  const mainRef = useScrollToTopOnNavigate();
  // Large screens: the sidebar toggle. Phones and tablets: the bottom tabs; "More" opens this sheet.
  const menu = <SidebarToggle collapsed={sidebar.collapsed} onToggle={sidebar.toggle} />;
  const sheet = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" className="pb-[max(1rem,env(safe-area-inset-bottom))]">
        <SheetTitle asChild>
          <div>
            <Brand />
          </div>
        </SheetTitle>
        <SheetDescription className="sr-only">{t.nav.label}</SheetDescription>
        <NavLinks onNavigate={() => setOpen(false)} />
        <UserMenu />
      </SheetContent>
    </Sheet>
  );
  return (
    <div className="flex h-dvh flex-col overflow-hidden pt-[env(safe-area-inset-top)]">
      <a
        href="#content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-card focus:px-3 focus:py-2"
      >
        {t.app.skipToContent}
      </a>
      <DemoBanner />
      <div className="flex min-h-0 flex-1">
        <aside
          id="sidebar"
          className={cn(
            'hidden w-60 shrink-0 flex-col justify-between overflow-y-auto border-r bg-card p-3',
            !sidebar.collapsed && 'lg:flex',
          )}
        >
          <div className="space-y-6">
            <Brand />
            <NavLinks />
          </div>
          <UserMenu />
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar menu={menu} />
          <main
            ref={mainRef}
            id="content"
            tabIndex={-1}
            className="relative min-h-0 flex-1 overflow-y-auto scroll-smooth overscroll-contain focus:outline-none"
          >
            <div
              key={pathname}
              className="page-enter mx-auto w-full max-w-7xl space-y-4 p-3 sm:p-4 lg:p-6"
            >
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      <BottomNav onMore={() => setOpen(true)} />
      {sheet}
    </div>
  );
}
